import React, { useEffect, useState, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, ArrowRight, Share2, Twitter, Linkedin, Facebook, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBlogPosts } from '../hooks/useBlogPosts';
import { trackBlogRead } from '../hooks/useAnalytics';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CTASection } from '../components/sections/CTASection';
const categoryColors: Record<string, string> = {
  Strategy: 'bg-[#0D9488] text-white',
  Design: 'bg-[#FF6600] text-white',
  Development: 'bg-[#00316B] text-white',
  Branding: 'bg-[#7C3AED] text-white'
};
function renderBody(body: string) {
  return body.split('\n\n').map((block, i) => {
    if (block.startsWith('## ')) {
      return <h2 key={i} className="text-2xl font-bold text-[#0C0C0C] mt-10 mb-4 font-heading" data-id="element-510">
          {block.replace('## ', '')}
        </h2>;
    }
    if (block.startsWith('**') && block.endsWith('**')) {
      return <p key={i} className="font-semibold text-[#0C0C0C] mb-4" data-id="element-511">
          {block.replace(/\*\*/g, '')}
        </p>;
    }
    // Handle inline bold
    const parts = block.split(/(\*\*[^*]+\*\*)/);
    return <p key={i} className="text-gray-700 leading-relaxed mb-6 text-lg" data-id="element-512">
        {parts.map((part, j) => part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold text-[#0C0C0C]" data-id="element-513">
              {part.replace(/\*\*/g, '')}
            </strong> : part)}
      </p>;
  });
}
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    posts
  } = useBlogPosts();
  const post = posts.find(p => p.slug === slug);
  // Track blog post read
  useEffect(() => {
    if (slug) {
      trackBlogRead(slug);
    }
  }, [slug]);
  if (!post) {
    return <main className="min-h-screen bg-[#F8F8F6] pt-32 flex flex-col items-center justify-center text-center px-4" data-id="element-514">
        <h1 className="text-4xl font-bold text-[#0C0C0C] mb-4" data-id="element-515">
          Post Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-516">
          This article doesn't exist or may have been moved.
        </p>
        <Button onClick={() => navigate('/blog')} data-id="element-517">Back to Blog</Button>
      </main>;
  }
  const related = posts.filter(p => p.id !== post.id && p.category === post.category).slice(0, 2);
  const fallbackRelated = posts.filter(p => p.id !== post.id).slice(0, 2);
  const relatedPosts = related.length >= 2 ? related : fallbackRelated;
  return <main className="bg-[#F8F8F6] min-h-screen" data-id="element-518">
      {/* Hero */}
      <div className="bg-[#00316B] pt-28 pb-0 relative overflow-hidden" data-id="element-519">
        <div className="absolute inset-0 opacity-10" data-id="element-520">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-521">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-522" />
          </svg>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-16" data-id="element-523">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-524">
            <Link to="/blog" className="inline-flex items-center text-blue-200 hover:text-white transition-colors text-sm mb-8 group" data-id="element-525">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-526" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-6" data-id="element-527">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[post.category] ?? 'bg-gray-200 text-gray-800'}`} data-id="element-528">
                {post.category}
              </span>
              <span className="text-blue-200 text-sm flex items-center gap-1" data-id="element-529">
                <Clock className="w-3 h-3" data-id="element-530" /> {post.readTime}
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6 font-heading" data-id="element-531">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-blue-200 text-sm" data-id="element-532">
              <span className="flex items-center gap-1.5" data-id="element-533">
                <User className="w-4 h-4" data-id="element-534" /> {post.author}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-535">
                <Calendar className="w-4 h-4" data-id="element-536" /> {post.date}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Cover Image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-1" data-id="element-537">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-538">
          <img src={post.image} alt={post.title} className="w-full h-64 md:h-96 object-cover" data-id="element-539" />
        </motion.div>
      </div>

      {/* Article Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-540">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-16" data-id="element-541">
          {/* Content */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-542">
            <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-[#FF6600] pl-6" data-id="element-543">
              {post.excerpt}
            </p>
            <div className="prose-content" data-id="element-544">{renderBody(post.body)}</div>

            {/* Share / Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-545">
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${categoryColors[post.category] ?? 'bg-gray-200 text-gray-800'}`} data-id="element-546">
                {post.category}
              </span>
              <Link to="/blog" data-id="element-547">
                <Button variant="outline" size="sm" className="group" data-id="element-548">
                  More Articles
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-549" />
                </Button>
              </Link>
            </div>

            <ShareButtons title={post.title} data-id="element-550" />
          </motion.article>

          {/* Sidebar */}
          <aside className="space-y-8" data-id="element-551">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-552">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4" data-id="element-553">
                About the Author
              </h3>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 shadow-sm" data-id="element-554">
                <div className="w-12 h-12 rounded-full bg-[#00316B] flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-555">
                  {post.author.charAt(0)}
                </div>
                <div data-id="element-556">
                  <p className="font-semibold text-[#0C0C0C]" data-id="element-557">{post.author}</p>
                  <p className="text-sm text-gray-500" data-id="element-558">Belo Digital Team</p>
                </div>
              </div>
            </motion.div>

            {relatedPosts.length > 0 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-559">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4" data-id="element-560">
                  Related Articles
                </h3>
                <div className="space-y-4" data-id="element-561">
                  {relatedPosts.map(related => <Link key={related.id} to={`/blog/${related.slug}`} data-id="element-562">
                      <Card className="p-4 group cursor-pointer hover:border-blue-100 transition-colors" hoverEffect data-id="element-563">
                        <div className="flex gap-3" data-id="element-564">
                          <img src={related.image} alt={related.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" data-id="element-565" />
                          <div data-id="element-566">
                            <p className="text-xs text-[#FF6600] font-semibold mb-1" data-id="element-567">
                              {related.category}
                            </p>
                            <p className="text-sm font-semibold text-[#0C0C0C] group-hover:text-[#00316B] transition-colors line-clamp-2 leading-snug" data-id="element-568">
                              {related.title}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </Link>)}
                </div>
              </motion.div>}

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.6
          }} className="bg-[#00316B] rounded-2xl p-6 text-white" data-id="element-569">
              <h3 className="font-bold text-lg mb-2" data-id="element-570">
                Have a project in mind?
              </h3>
              <p className="text-blue-200 text-sm mb-4" data-id="element-571">
                Let's build something great together.
              </p>
              <Link to="/contact" data-id="element-572">
                <Button size="sm" className="bg-[#FF6600] hover:bg-[#CC5200] text-white w-full" data-id="element-573">
                  Start a Project
                </Button>
              </Link>
            </motion.div>
          </aside>
        </div>
      </div>

      <CTASection data-id="element-574" />
    </main>;
}